import ICache from '../ICache';
import encryptor from '@/library/Encryptor';
import { openDB, deleteDB } from 'idb';
import { IActivity } from '@/domain/StravaTypes';

class IndexedDbCache implements ICache {
    private db;

    constructor() {
        if (!('indexedDB' in window)) {
            throw new Error('This browser doesn\'t support IndexedDB');
        }
    }

    public async getSegment(id: number): Promise<{}> {
        return this.dbGet('segments', id);
    }

    public async setSegment(id: number, activity: {}): Promise<{}> {
        return this.dbSet('segments', id, activity);
    }

    public async getActivity(id: number): Promise<IActivity> {
        return this.dbGet('activities', id);
    }

    public async setActivity(id: number, activity: {}): Promise<{}> {
        return this.dbSet('activities', id, activity);
    }

    public async getActivitySummaries(key: string): Promise<[]> {
        return this.dbGet('activity-summaries', key);
    }

    public async getAllActivitySummaries(): Promise<[]> {
        return this.dbGetAll('activity-summaries');
    }

    public async setActivitySummaries(key: string, activitySummaries: []) {
        return this.dbSet('activity-summaries', key, activitySummaries);
    }


    public async getActivityStreams(id: number): Promise<any[]> {
        return this.dbGet('activity-streams', id);
    }

    public async getSegmentStreams(id: number): Promise<any[]> {
        return this.dbGet('segment-streams', id);
    }

    public async setActivityStreams(id: number, stream: {}): Promise<{}> {
        return this.dbSet('activity-streams', id, stream);
    }

    public async setSegmentStreams(id: number, stream: {}): Promise<{}> {
        return this.dbSet('segment-streams', id, stream);
    }

    public async destroy() {
        await deleteDB('strava-carbon');
    }

    private async connect() {
        const options = {
            upgrade: (db, oldVersion, newVersion, transaction) => {
                switch (oldVersion) {
                    case 0:
                        db.createObjectStore('activity-summaries');
                    case 1:
                        db.createObjectStore('activities');
                    case 2:
                        db.createObjectStore('activity-streams');
                    case 3:
                        db.createObjectStore('segment-streams');
                    case 4:
                        db.createObjectStore('segments');
                    case 5:
                }
            },
        };
        return openDB('strava-carbon', 5, options);
    }

    private async dbGet(table: string, key: any) {
        const db = await this.connect();
        const tx = db.transaction(table, 'readwrite');
        const data = await tx.objectStore(table).get(key);
        return data ? encryptor.decrypt(data) : undefined;
    }

    private async dbGetAll(table: string) {
        const db = await this.connect();
        const tx = db.transaction(table, 'readwrite');
        const data = await tx.objectStore(table).getAll();
        let result = undefined;
        if (data && data.length > 0) {
            result = data.map((d) => encryptor.decrypt(d));
        }
        return result;
    }

    private async dbSet(table: string, key: any, data: any) {
        const db = await this.connect();
        const tx = db.transaction(table, 'readwrite');
        return tx.objectStore(table).put(encryptor.encrypt(data), key);
    }
}

const cache = new IndexedDbCache();

export default cache;
